import {
    Component,
    ViewChild,
    OnInit,
    OnDestroy,
    Inject,
    Output,
    EventEmitter,
    Injector,
} from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';

import { Subscription, BehaviorSubject, combineLatest, switchMap, Observable, forkJoin, of, firstValueFrom } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { DistributionService } from '@Common/TargetingShared/services/distribution.service';
import { DistributionModel } from '../../../models/distribution.model';
import { DropdownTypesEnum } from '@Common/Forms/enums/dropdown-types.enum';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { FilterChipsComponent } from '@Common/Filter/components/filterChips/filter-chips.component';
import { AgprComponentStyleEnum } from '@Common/Basic/enums/social-analysis-types.enum';
import { TwitterAvatarSize } from '@Common/Misc/pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { EntityTypeDefaultImageEnum } from '@Common/Misc/enums/entity-type-default-image.enum';
import { TargetingRhpDialogService } from '@Common/TargetingShared/services/targeting-rhp-dialog.service';
import { EntityType } from '@api/targeting/enum/entity.type.enum';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import {
    RecipientPersonalizationDialogComponent,
    RecipientPersonalizationTab
} from '../RecipientPersonalizationDialog/recipient-personalization-dialog.component';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { FallbackPersonalizationsDialogComponent } from '@Common/Dialogs/components/FallbackPersonalizationsDialog/fallback-personalizations-dialog.component';
import { PreviewTextDialogComponent } from '@Common/Dialogs/components/PreviewTextDialog/preview-text-dialog.component';
import { PersonalizationWorkbenchDialogComponent } from '../PersonalizationWorkbenchDialog/personalization-workbench-dialog.component';
import { PersonalizationService } from '@Discover/Targeting/services/personalization.service';
import { RecipientPersonalization } from '@Discover/Targeting/models/personalization.model';
import { PersonalizationSourceTypeEnum } from '@api/distributions/enum/personalization-source-type.enum';
import { OutletModel } from '@Discover/Targeting/models/outlet.model';
import { ContactModel } from '@Common/TargetingShared/model/contact.model';
import { TargetingRhpContextsService, OutletContactTabType } from '@Common/TargetingShared/services/targeting-rhp-contexts.service';
import { PersonalizationTagEnum } from '@Discover/Targeting/enums/personalization-tag.enum';
import { RecipientEditorService } from '../RecipientEditor/recipient-editor.service';
import { RecipientInformationModel } from '../../../models/recipient-information.model';
import { RecipientType } from '../../../enums/recipient-type.enum';
import { ToggleAction } from '../../../enums/toggle-action.enum';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { DistributionApi } from '@api/distributions/distribution.api';
import {
    PersonalizationValue,
    PersonalizationSelection,
    PatchPersonalization,
    PersonalizationSummary
} from '@api/distributions/model/distribution-personalization.api.model';
import { ContactsPublicApi } from '@api/targeting/contacts-public.api';
import { OutletsPublicApi } from '@api/targeting/outlets-public.api';
import { FilterService } from '@Common/Filter/services/abstract-filter.service';
import { ScrollPanelComponent } from '@Common/Basic/components/ScrollPanel/scroll-panel.component';
import { TargetingResultFactory } from '@Discover/Targeting/factories/targeting-result.factory';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';
import { GLOBAL_URIS } from '@Common/Misc/helpers/formatting/global-uri.helper';


/**
 * This component is the main view for displaying personalizations for distribution recipients.
 * Their are 4 source types when saving personalizations, see PersonalizationSourceTypeEnum
 * - System - Journodex value (readonly)
 * - Fallback - Distribution fallback value
 * - Custom - Custom value for current distribution
 * - Entity Value - Profile value that will be saved to the recipient profile as private data for future use
 */
@Component({
    selector: 'agpr-personalize',
    templateUrl: './personalize.component.html',
    styleUrls: ['./personalize.component.scss'],
    providers: [
        {
            provide: FilterService,
            useExisting: PersonalizationService
        },
        {
            provide: 'PaginatorServiceInterface',
            useExisting: PersonalizationService
        },
        {
            provide: 'SortOrderServiceInterface',
            useExisting: PersonalizationService
        },
    ],
    standalone: false
})
export class PersonalizeComponent implements OnInit, OnDestroy {

    @Output() moveToRecipientStep = new EventEmitter<void>();
    @Output() saveDistribution = new EventEmitter<void>();
    @Output() hidePersonalizeSideBar = new EventEmitter<boolean>();

    @ViewChild('filterChipsComponent') filterChipsComponent: FilterChipsComponent;
    @ViewChild('listViewPanel') listViewPanel: ScrollPanelComponent;

    overlayContainer: Element;
    screenWidth: number;
    loading: boolean = false;
    distributionLoading: boolean = false;

    recipientPersonalizations: RecipientPersonalization[] = [];

    personalizationOptions: DropdownOptionModel<PersonalizationTagEnum>[] = [];
    selectedPersonalization: DropdownOptionModel<PersonalizationTagEnum>;
    unsavedRecipients: number = 0;
    unsavedRecipientIds: string[] = [];

    selectedRecipientId: string;

    distribution: DistributionModel;

    distributionDirty: boolean = false;
    noRecipients: boolean = false;

    unlockedSearches: boolean = false;
    showAiHelper: boolean = false;

    total: number = 0;

    private detailSummary: PersonalizationSummary[] = [];
    private recipientInformation: RecipientInformationModel;
    private subscriptions: Subscription[] = [];
    private animationTrigger = new BehaviorSubject(
        new FullProfileAnimationModel(
            FullProfileDialogAnimationsEnum.slideInOutAnimation,
            OverlayAnimationStateEnum.open
        )
    );

    // used to prevent user from opening the recipient view before any update calls have been completed
    // since its the same view (ie same data), make sure update calls complete before fetching
    private updateInProgress$ = new BehaviorSubject<boolean>(false);

    readonly id = 'personalize';
    readonly buttonType = ButtonTypesEnum;
    readonly dropdownType = DropdownTypesEnum;
    readonly agprComponentStyleEnum = AgprComponentStyleEnum;
    readonly personalizationTag = PersonalizationTagEnum;
    readonly twitterAvatarSize = TwitterAvatarSize;
    readonly imageDefault = EntityTypeDefaultImageEnum;
    readonly personalizationSourceTypeEnum = PersonalizationSourceTypeEnum;
    readonly entityType = EntityType;
    readonly MAX_TEXT_COUNT: number = 176;
    readonly personalizeTabIndex: number = 1;
    readonly allowedContactProfileSaveTags = [
        PersonalizationTagEnum.PREFIX,
        PersonalizationTagEnum.FNAME,
        PersonalizationTagEnum.LNAME,
        PersonalizationTagEnum.OUTLET_NAME
    ];
    readonly allowedOutletProfileSaveTags = [
        PersonalizationTagEnum.OUTLET_NAME
    ];


    constructor(
        private coreDialogService: CoreDialogService,
        private distributionService: DistributionService,
        private distributionApi: DistributionApi,
        private targetingRhpDialogService: TargetingRhpDialogService,
        private personalizationService: PersonalizationService,
        private targetingRhpContextsService: TargetingRhpContextsService,
        private recipientEditorService: RecipientEditorService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private contactsPublicApi: ContactsPublicApi,
        private outletsPublicApi: OutletsPublicApi,
        private targetingResultFactory: TargetingResultFactory,
        private currentUserStateService: CurrentUserStateService,
        public injector: Injector,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {}

    ngOnInit() {
        this.personalizationService.setPageCriteria({page: 1, pageSize: 25}, false);
        this.overlayContainer = document.getElementById('main-content');

        this.subscriptions.push(
            combineLatest([
                this.distributionService.getDirtyFlag(),
                this.distributionService.getFocusedResource(),
                this.recipientEditorService.getCurrentRecipientInformation().pipe(filter(x => !!x))
            ]).subscribe(([dirty, distribution, recipientInfo]) => {
                this.distributionDirty = dirty;
                this.distribution = distribution;
                this.recipientInformation = recipientInfo;

                const searchGroup = this.recipientInformation.recipientGroups.find(x => x.type === RecipientType.search);
                this.unlockedSearches = searchGroup ? searchGroup.recipients.some(x => !x.isLocked) : false;

                this.noRecipients = this.distribution?.recipientInformation?.total === 0;

                const hypePageDisplayed =
                    this.distributionDirty ||
                    this.noRecipients ||
                    this.unlockedSearches;

                this.hidePersonalizeSideBar.emit(hypePageDisplayed);
            }),
            this.personalizationService.getPersonalizationTags().pipe(
                switchMap((personalizationTags) => {
                    this.personalizationOptions = personalizationTags.map(type => {
                        return new DropdownOptionModel(`Discover.personalizationTag.${type}`, type);
                    });

                    if (this.personalizationOptions.length === 0) {
                        this.hidePersonalizeSideBar.emit(true);
                        return of([]);
                    }

                    this.onPersonalizationChange(this.personalizationOptions[0]);

                    return this.personalizationService.getRecipientPersonalizations();
                })
            ).subscribe((recipientPersonalizations) => {
                this.recipientPersonalizations = recipientPersonalizations;

                if (this.listViewPanel) {
                    this.listViewPanel.scrollTo(0, 0, false);
                }

                if (this.recipientPersonalizations.length > 0) {
                    this.cardFocused(this.recipientPersonalizations[0].recipient);
                    this.targetingRhpContextsService.selectOutletContactTab(null);
                    this.targetingRhpContextsService.selectTab(this.personalizeTabIndex);
                } else {
                    // we want to reset the RHP if there is no recipient in the personalizations
                    // else when you switch betweem dists, and the RHP could not clear and show the old recipient info
                    this.targetingRhpContextsService.setEntityByResult(null);
                    this.targetingRhpContextsService.selectOutletContactTab(null);
                    this.targetingRhpContextsService.selectTab(this.personalizeTabIndex);
                }

                this.updateSaveCount();

            }),
            this.personalizationService.getItemCount().subscribe(x => this.total = x),
            this.personalizationService.getSummary().subscribe(summary => this.detailSummary = summary),
            this.distributionService.getLoading().subscribe(x => this.distributionLoading = x),
            this.personalizationService.getSearchLoading().subscribe(x => this.loading = x),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.showAiHelper = permissions.includes(userPermissionsEnum.openAI);
                }
            }),
        );
    }

    onPersonalizationChange(personalization: DropdownOptionModel<PersonalizationTagEnum>) {
        this.selectedPersonalization = personalization;
        this.personalizationService.selectedPersonalizationTag = personalization.value;
        this.personalizationService.doSearch();
    }


    cardFocused(entity: ContactModel | OutletModel) {
        if (this.selectedRecipientId === entity.id) {
            return;
        }

        this.selectedRecipientId = entity.id;

        if (entity.entityType === EntityType.CONTACT) {
            const targetingEntity = this.targetingResultFactory.fromContactModel(entity as ContactModel);
            this.targetingRhpContextsService.setEntityByResult(targetingEntity);
        } else {
            this.targetingRhpContextsService.setOutlet(entity.id);
        }
    }

    /** Save the custom value to the entities profile privatedata */
    saveToProfile(recipientPersonalization: RecipientPersonalization) {
        const privateData: PatchPersonalization = {
            personalizationsToUpsert: [
                {
                    tagType: recipientPersonalization.personalization.tagType,
                    value: recipientPersonalization.personalization.custom
                }
            ]
        };

        let apiCall: Observable<string>;
        let nameLabel: string = '';

        if (recipientPersonalization.recipient.entityType === EntityType.CONTACT) {
            nameLabel = `${(recipientPersonalization.recipient as ContactModel).firstName} ${(recipientPersonalization.recipient as ContactModel).lastName}`;
            apiCall = this.contactsPublicApi.updatePersonalizations(
                recipientPersonalization.recipient.id,
                privateData
            );
        } else {
            nameLabel = recipientPersonalization.recipient.name;
            apiCall = this.outletsPublicApi.updatePersonalizations(
                recipientPersonalization.recipient.id,
                privateData
            );
        }

        apiCall.pipe(
            catchError((error: HttpErrorResponse) => this.personalizationService.handleUpsertError(
                error, privateData.personalizationsToUpsert[0], nameLabel
            )),
        ).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    detail: 'Discover.distribution.personalizationSaved.heading',
                    summary: this.localizationService.transform('Discover.distribution.personalizationSaved.body', { entity: nameLabel }),
                });

                recipientPersonalization.personalization.profile = recipientPersonalization.personalization.custom;

                this.updateSaveCount();
            },
        });
    }


    saveAll() {
        const apiCalls: Observable<string>[] = this.recipientPersonalizations
            .filter(x => this.unsavedRecipientIds.includes(x.recipient.id))
            .map(recipientPersonalization => {
                const privateData: PatchPersonalization = {
                    personalizationsToUpsert: [
                        {
                            tagType: recipientPersonalization.personalization.tagType,
                            value: recipientPersonalization.personalization.custom
                        }
                    ]
                };

                let nameLabel = '';
                if (recipientPersonalization.recipient.entityType === EntityType.CONTACT) {
                    // eslint-disable-next-line max-len
                    nameLabel = `${(recipientPersonalization.recipient as ContactModel).firstName} ${(recipientPersonalization.recipient as ContactModel).lastName}`;
                    return this.contactsPublicApi.updatePersonalizations(
                        recipientPersonalization.recipient.id,
                        privateData
                    ).pipe(
                        catchError((error: HttpErrorResponse) => this.personalizationService.handleUpsertError(
                            error, privateData.personalizationsToUpsert[0], nameLabel
                        )),
                    );
                } else {
                    nameLabel = recipientPersonalization.recipient.name;
                    return this.outletsPublicApi.updatePersonalizations(
                        recipientPersonalization.recipient.id,
                        privateData
                    ).pipe(
                        catchError((error: HttpErrorResponse) => this.personalizationService.handleUpsertError(
                            error, privateData.personalizationsToUpsert[0], nameLabel
                        )),
                    );
                }
            });


        forkJoin(apiCalls).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    detail: 'Discover.distribution.personalizationSaveAll.heading',
                    summary: this.localizationService.transform('Discover.distribution.personalizationSaveAll.body', { count: apiCalls.length }),
                });

                this.recipientPersonalizations
                    .filter(x => this.unsavedRecipientIds.includes(x.recipient.id))
                    .forEach(recipientPersonalization => {
                        recipientPersonalization.personalization.profile = recipientPersonalization.personalization.custom;
                    });

                this.updateSaveCount();
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    detail: 'Discover.distribution.personalizationSaveAll.error.heading',
                });
            }
        });
    }


    updateSelection(recipientPersonalization: RecipientPersonalization) {

        const personalizationSelection: PersonalizationSelection = {
            tagType: this.selectedPersonalization.value,
            selectionType: recipientPersonalization.personalization.selectedSource,
        };


        if (recipientPersonalization.recipient.entityType === EntityType.CONTACT) {
            this.distributionApi.upsertContactPersonalizationSelections(
                this.distribution.id,
                recipientPersonalization.recipient.id,
                personalizationSelection
            ).subscribe();
        } else {
            this.distributionApi.upsertOutletPersonalizationSelections(
                this.distribution.id,
                recipientPersonalization.recipient.id,
                personalizationSelection
            ).subscribe();
        }

        const previousSelection = recipientPersonalization.personalization.selectedSource === PersonalizationSourceTypeEnum.CUSTOM ?
            PersonalizationSourceTypeEnum.SYSTEM : PersonalizationSourceTypeEnum.CUSTOM;

        let previouslyCounted: boolean;
        if (previousSelection === PersonalizationSourceTypeEnum.CUSTOM) {
            previouslyCounted = (recipientPersonalization.personalization.custom.length > 0);
        } else {
            previouslyCounted = (recipientPersonalization.personalization.system.length > 0);
        }

        let currentlyCounted: boolean;
        if (recipientPersonalization.personalization.selectedSource === PersonalizationSourceTypeEnum.CUSTOM) {
            currentlyCounted = (recipientPersonalization.personalization.custom.length > 0);
        } else {
            currentlyCounted = (recipientPersonalization.personalization.system.length > 0);
        }

        const tagSummary = this.detailSummary.find(x => x.tagType === this.selectedPersonalization.value);

        // update count if differential present
        if (tagSummary && !previouslyCounted && currentlyCounted) {
            tagSummary.totalPersonalizations++;
        } else if (tagSummary && previouslyCounted && !currentlyCounted) {
            tagSummary.totalPersonalizations--;
        }

        this.personalizationService.updateSummary(this.detailSummary);
    }

    switchToCustom(recipientPersonalization: RecipientPersonalization) {
        if (recipientPersonalization.personalization.selectedSource === PersonalizationSourceTypeEnum.CUSTOM) {

            return;
        }

        recipientPersonalization.personalization.selectedSource = PersonalizationSourceTypeEnum.CUSTOM;
        this.updateSelection(recipientPersonalization);
    }


    /** Save the personalization custom value (PersonalizationSourceTypeEnum.CUSTOM) of a recipient */
    customChange(recipientPersonalization: RecipientPersonalization) {

        this.updateInProgress$.next(true);

        let apiCall: Observable<void>;

        const personalization: PersonalizationValue = {
            tagType: recipientPersonalization.personalization.tagType,
            value: recipientPersonalization.personalization.custom
        };

        let nameLabel = '';
        if (recipientPersonalization.recipient.entityType === EntityType.CONTACT) {
            nameLabel = `${(recipientPersonalization.recipient as ContactModel).firstName} ${(recipientPersonalization.recipient as ContactModel).lastName}`;
            apiCall = this.distributionApi.updateContactPersonalization(
                this.distribution.id,
                recipientPersonalization.recipient.id,
                personalization
            );
        } else {
            nameLabel = recipientPersonalization.recipient.name;
            apiCall = this.distributionApi.updateOutletPersonalization(
                this.distribution.id,
                recipientPersonalization.recipient.id,
                personalization
            );
        }


        apiCall.pipe(
            catchError((error: HttpErrorResponse) => {
                recipientPersonalization.personalization.custom = recipientPersonalization.personalization.previousCustom;

                return this.personalizationService.handleUpsertError(error, personalization, nameLabel);
            }),
            finalize(() => this.updateInProgress$.next(false))
        ).subscribe({
            next: () => {
                const previouslyCounted = recipientPersonalization.personalization.previousCustom.length > 0;
                const currentlyCounted = recipientPersonalization.personalization.custom.length > 0;

                const tagSummary = this.detailSummary.find(x => x.tagType === this.selectedPersonalization.value);

                // update count if differential present
                if (!previouslyCounted && currentlyCounted) {
                    tagSummary.totalPersonalizations++;
                } else if (previouslyCounted && !currentlyCounted) {
                    tagSummary.totalPersonalizations--;
                }

                this.personalizationService.updateSummary(this.detailSummary);
                recipientPersonalization.personalization.previousCustom = recipientPersonalization.personalization.custom;

                this.updateSaveCount();
            }
        });
    }

    /**
     * Clears the current filters
     */
    clearFilters() {
        this.filterChipsComponent?.chipContainer.chipPopover.hide();
        this.personalizationService.clearFilters();
        this.personalizationService.applyFilters();
    }


    focusOutlet(recipient: OutletModel | ContactModel) {
        this.cardFocused(recipient);
        this.targetingRhpDialogService.openTakeOver(
            {
                inputs: {
                    contactId: null,
                    outletId: (recipient as ContactModel).selectedEmployment?.outlet.id,
                    entityType : EntityType.OUTLET,
                    isPrivate: false
                }
            }
        );
    }

    editFallback() {
        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
                saveOnComplete: true,
            },
            outputs: {
                dialogClose: (save: boolean) => {
                    if (save) {
                        this.personalizationService.doSearch();
                    }
                }
            }
        };
        this.coreDialogService.open(FallbackPersonalizationsDialogComponent, io);
    }

    async editRecipient(entity: ContactModel | OutletModel) {
        await firstValueFrom(
            this.updateInProgress$.pipe(
                filter(inProgress => inProgress === false)
            )
        );

        const io = {
            inputs: {
                currentEntity: entity,
                distributionId: this.distribution.id,
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
            },
            outputs: {
                dialogClose: () => {
                    // search call needs to be triggered to get saved changes made in dialog
                    this.personalizationService.doSearch();
                }
            }
        };
        this.coreDialogService.open(RecipientPersonalizationDialogComponent, io, this.injector);
    }

    viewComponsition(entity: ContactModel | OutletModel) {
        const io = {
            inputs: {
                currentEntity: entity,
                selectedTab: RecipientPersonalizationTab.preview,
                distributionId: this.distribution.id,
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
            },
            outputs: {
                dialogClose: () => {
                    // search call needs to be triggered to get saved changes made in dialog
                    this.personalizationService.doSearch();
                }
            }
        };
        this.coreDialogService.open(RecipientPersonalizationDialogComponent, io, this.injector);
    }

    learnMore() {
        window.open(GLOBAL_URIS.personalizationsHelp, '_blank');
    }

    navigateToRecipientStep() {
        this.moveToRecipientStep.emit();
    }


    openPreview(
        text: string,
        entity: ContactModel | OutletModel
    ) {
        let nameLabel: string = '';
        if (entity.entityType === EntityType.CONTACT) {
            nameLabel = `${(entity as ContactModel).firstName} ${(entity as ContactModel).lastName}`;
        } else {
            nameLabel = entity.name;
        }

        const personalizedType = this.localizationService.transform(`Discover.personalizationTag.${this.selectedPersonalization.value}`);

        const io = {
            inputs: {
                content: text,
                heading: this.localizationService.transform('Discover.distribution.personalizationPreview', { name: nameLabel, type: personalizedType })
            },
            outputs: {
                dialogClose: () => {}
            }
        };

        this.coreDialogService.open(PreviewTextDialogComponent, io);
    }


    aiRefinement(
        personalizationTag: PersonalizationTagEnum.INTRO | PersonalizationTagEnum.OUTRO,
        recipientPersonalization: RecipientPersonalization
    ) {
        this.cardFocused(recipientPersonalization.recipient);

        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
                personalizationType: personalizationTag,
                distributionId: this.distribution.id,
                aiEnabled: this.showAiHelper,
                userText: recipientPersonalization.personalization.custom,
                recipient: recipientPersonalization.recipient
            },
            outputs: {
                contentChanges: (content: string) => {
                    recipientPersonalization.personalization.custom = content;
                    this.switchToCustom(recipientPersonalization);
                    this.customChange(recipientPersonalization);
                }
            }
        };

        this.coreDialogService.open(PersonalizationWorkbenchDialogComponent, io);
    }

    lockAllSearches() {
        const searchGroup = this.recipientInformation.recipientGroups.find(x => x.type === RecipientType.search);
        const searchIds = searchGroup.recipients.map(x => x.targetingSearchId);
        this.recipientEditorService.toggleSearches(searchIds, ToggleAction.LOCK).subscribe(
            x => this.personalizationService.doSearch()
        );
    }


    private updateSaveCount() {
        this.unsavedRecipientIds = [];
        this.unsavedRecipients = this.recipientPersonalizations.reduce(
            (total, recipientPersonalization) => {

                const showSave = (
                    recipientPersonalization.personalization.custom !== recipientPersonalization.personalization.profile &&
                    (
                        (recipientPersonalization.recipient.entityType === EntityType.CONTACT &&
                        this.allowedContactProfileSaveTags.includes(recipientPersonalization.personalization.tagType)) ||
                        (recipientPersonalization.recipient.entityType === EntityType.OUTLET &&
                        this.allowedOutletProfileSaveTags.includes(recipientPersonalization.personalization.tagType))
                    )
                );

                if (showSave) {
                    this.unsavedRecipientIds.push(recipientPersonalization.recipient.id);
                }

                return total + (showSave ? 1 : 0);
            }, 0
        );
    }


    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
